import { Subject, takeUntil } from 'rxjs';
import { animate, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { UserService } from 'src/app/Services/user.service';
import { AuthMethods, CreateUserRequest, UpdateUserRequest } from 'src/app/Shared/Models/User.model';
import { PrimeNgModule } from 'src/app/Shared/prime-ng/prime-ng.module';

interface SelectOption<T = string> {
  label: string;
  value: T;
}

@Component({
  selector: 'app-user-creation',
  standalone: true,
  imports: [
    PrimeNgModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './user-detail.component.html',
  styleUrls: ['./user-detail.component.css'],
  animations: [
    trigger('cardAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
  ],
})
export class UserDetailComponent implements OnInit, OnDestroy {
  createUserForm: FormGroup;
  loading = false;
  error = '';
  success = '';

  isEditMode = false;
  userId: string | null = null;

  resetPasswordDialog = false;
  newPassword = '';
  confirmPassword = '';
  passwordError = '';
  showNewPassword = false;
  showConfirmPassword = false;

  resetMfaDialog = false;

  roleMapping: { dbName: string; displayName: string }[] = [
    { dbName: 'ADMIN', displayName: 'Admin' },
    { dbName: 'Inspector', displayName: 'Inspector' },
    { dbName: 'Auditor', displayName: 'Auditor' },
    { dbName: 'ExternalInspector', displayName: 'External Inspector' },
  ];

  roles: string[] = [];
  roleOptions: SelectOption[] = [];
  authMethodOptions: SelectOption<AuthMethods>[] = [
    { label: 'App login', value: 'App' },
    { label: 'Microsoft login', value: 'Azure' },
  ];
  roleDbNames: { [key: string]: string } = {};
  roleDisplayNames: { [key: string]: string } = {};

  private destroy$ = new Subject<void>();

  constructor(
    private userService: UserService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.createUserForm = this.fb.group({
      username: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      password: ['', [Validators.required, Validators.minLength(8)]],
      role: ['', Validators.required],
      authMethods: ['App', Validators.required],
      userPrincipalName: [''],
      objectId: [''],
      mfaEnabled: [false],
    });
  }

  ngOnInit(): void {
    this.userId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.userId;

    this.createUserForm.get('authMethods')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((authMethod: AuthMethods) => this.syncPasswordValidators(authMethod));

    this.createUserForm.get('email')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(email => {
        if (!this.isEditMode && this.selectedAuthMethod === 'Azure' && !this.createUserForm.get('userPrincipalName')?.value) {
          this.createUserForm.patchValue({ userPrincipalName: email }, { emitEvent: false });
        }
      });

    this.syncPasswordValidators(this.createUserForm.get('authMethods')?.value);
    this.loadRoles();

    if (this.isEditMode && this.userId) {
      this.loadUser(this.userId);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get selectedAuthMethod(): AuthMethods {
    return (this.createUserForm.get('authMethods')?.value || 'App') as AuthMethods;
  }

  private loadRoles(): void {
    this.userService.getRoles()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rolesFromApi: any[]) => {
          this.roleOptions = [];
          this.roles = [];
          this.roleDbNames = {};
          this.roleDisplayNames = {};

          rolesFromApi.forEach(r => {
            const dbName = typeof r === 'string' ? r : r?.name;
            if (!dbName) return;

            const mapping = this.roleMapping.find(m => m.dbName === dbName);
            const label = mapping?.displayName || dbName;

            this.roleOptions.push({ label, value: dbName });
            this.roles.push(label);
            this.roleDbNames[label] = dbName;
            this.roleDisplayNames[dbName] = label;
          });

          if (this.roleOptions.length > 0 && !this.isEditMode && !this.createUserForm.value.role) {
            this.createUserForm.patchValue({ role: this.roleOptions[0].value });
          }
        },
        error: () => {
          this.error = 'Error loading roles';
          setTimeout(() => this.error = '', 3000);
        },
      });
  }

  private loadUser(userId: string): void {
    this.loading = true;
    this.userService.getUser(Number(userId))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: user => {
          this.createUserForm.patchValue({
            username: user.username,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role,
            authMethods: user.authMethods || 'App',
            userPrincipalName: user.userPrincipalName || user.email,
            objectId: user.objectId || '',
            mfaEnabled: user.mfaEnabled,
          });
          this.loading = false;
        },
        error: () => {
          this.error = 'Error loading user data';
          this.loading = false;
          setTimeout(() => this.error = '', 3000);
        },
      });
  }

  createUser(): void {
    if (this.createUserForm.invalid) {
      this.error = 'Please fill all fields correctly';
      setTimeout(() => this.error = '', 3000);
      return;
    }

    this.loading = true;
    this.error = '';
    this.success = '';

    const formValue = this.createUserForm.value;
    const authMethods = (formValue.authMethods || 'App') as AuthMethods;
    const dbRole = formValue.role;

    if (this.isEditMode && this.userId) {
      const updateData: UpdateUserRequest = {
        username: formValue.username,
        email: formValue.email,
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        role: dbRole,
        authMethods,
        userPrincipalName: authMethods === 'Azure' ? (formValue.userPrincipalName || formValue.email) : null,
        objectId: authMethods === 'Azure' ? (formValue.objectId || null) : null,
      };

      this.userService.updateUser(Number(this.userId), updateData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.success = `User "${this.createUserForm.value.username}" successfully updated!`;
            this.loading = false;
            setTimeout(() => {
              this.router.navigate(['/admin/user-overview']);
            }, 1500);
          },
          error: (err: any) => {
            this.error = err.error?.message || 'Error updating user';
            this.loading = false;
            setTimeout(() => this.error = '', 5000);
          },
        });

      return;
    }

    const createData: CreateUserRequest = {
      username: formValue.username,
      email: formValue.email,
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      role: dbRole,
      authMethods,
      userPrincipalName: authMethods === 'Azure' ? (formValue.userPrincipalName || formValue.email) : null,
      objectId: authMethods === 'Azure' ? (formValue.objectId || null) : null,
      mfaEnabled: false,
      isActive: true,
    };

    if (authMethods === 'App') {
      createData.password = formValue.password;
    }

    this.userService.createUser(createData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.success = `User "${this.createUserForm.value.username}" successfully created!`;
          this.createUserForm.reset({
            role: this.roleOptions[0]?.value || '',
            authMethods: 'App',
            userPrincipalName: '',
            objectId: '',
            mfaEnabled: false,
          });
          this.syncPasswordValidators('App');
          this.loading = false;
          setTimeout(() => {
            this.router.navigate(['/admin/user-overview']);
          }, 1500);
        },
        error: (err: any) => {
          this.error = err.error?.message || 'Error creating user';
          this.loading = false;
          setTimeout(() => this.error = '', 5000);
        },
      });
  }

  generatePassword(): void {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz123456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    this.createUserForm.patchValue({ password });
  }

  copyPassword(): void {
    const password = this.createUserForm.get('password')?.value;
    if (password) {
      navigator.clipboard.writeText(password);
      this.success = 'Password copied to clipboard!';
      setTimeout(() => this.success = '', 2000);
    }
  }

  openResetPasswordDialog(): void {
    this.resetPasswordDialog = true;
    this.newPassword = '';
    this.confirmPassword = '';
    this.passwordError = '';
    this.showNewPassword = false;
    this.showConfirmPassword = false;
  }

  closeResetPasswordDialog(): void {
    this.resetPasswordDialog = false;
    this.newPassword = '';
    this.confirmPassword = '';
    this.passwordError = '';
  }

  generateNewPassword(): void {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz123456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    this.newPassword = password;
  }

  copyNewPassword(): void {
    if (this.newPassword) {
      navigator.clipboard.writeText(this.newPassword);
      this.success = 'Password copied to clipboard!';
      setTimeout(() => this.success = '', 2000);
    }
  }

  confirmResetPassword(): void {
    this.passwordError = '';

    if (this.newPassword !== this.confirmPassword) {
      this.passwordError = 'Passwords do not match';
      return;
    }

    if (this.newPassword.length < 8) {
      this.passwordError = 'Password must be at least 8 characters';
      return;
    }

    if (!this.userId) return;

    this.userService.resetPassword(Number(this.userId), this.newPassword)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.success = 'Password reset successfully';
          this.error = '';
          this.closeResetPasswordDialog();
          setTimeout(() => this.success = '', 3000);
        },
        error: () => {
          this.passwordError = 'Error resetting password';
        },
      });
  }

  resetMfa(): void {
    if (!this.userId) return;

    if (confirm(`Reset MFA for user ${this.createUserForm.value.username}? User will need to set up MFA again on next login.`)) {
      this.userService.resetMFA(Number(this.userId))
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.success = 'MFA reset successfully';
            this.error = '';
            this.createUserForm.patchValue({ mfaEnabled: false });
            setTimeout(() => this.success = '', 3000);
          },
          error: () => {
            this.error = 'Error resetting MFA';
            this.success = '';
            setTimeout(() => this.error = '', 5000);
          },
        });
    }
  }

  goBack(): void {
    this.router.navigate(['/admin/user-overview']);
  }

  openResetMfaDialog(): void {
    this.resetMfaDialog = true;
  }

  closeResetMfaDialog(): void {
    this.resetMfaDialog = false;
  }

  confirmResetMfa(): void {
    if (!this.userId) return;
    this.userService.resetMFA(Number(this.userId))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.success = 'MFA reset successfully';
          this.error = '';
          this.createUserForm.patchValue({ mfaEnabled: false });
          this.closeResetMfaDialog();
          setTimeout(() => this.success = '', 3000);
        },
        error: () => {
          this.error = 'Error resetting MFA';
          this.success = '';
          this.closeResetMfaDialog();
          setTimeout(() => this.error = '', 5000);
        },
      });
  }

  private syncPasswordValidators(authMethod: AuthMethods = 'App'): void {
    const passwordControl = this.createUserForm.get('password');
    if (!passwordControl) return;

    if (this.isEditMode || authMethod === 'Azure') {
      passwordControl.clearValidators();
      if (authMethod === 'Azure') {
        passwordControl.reset('');
      }
    } else {
      passwordControl.setValidators([Validators.required, Validators.minLength(8)]);
    }

    passwordControl.updateValueAndValidity({ emitEvent: false });

    const userPrincipalNameControl = this.createUserForm.get('userPrincipalName');
    if (!userPrincipalNameControl) return;

    if (authMethod === 'Azure') {
      userPrincipalNameControl.setValidators([Validators.required]);
      if (!userPrincipalNameControl.value) {
        userPrincipalNameControl.setValue(this.createUserForm.get('email')?.value || '', { emitEvent: false });
      }
    } else {
      userPrincipalNameControl.clearValidators();
      userPrincipalNameControl.setValue('', { emitEvent: false });
      this.createUserForm.get('objectId')?.setValue('', { emitEvent: false });
    }

    userPrincipalNameControl.updateValueAndValidity({ emitEvent: false });
  }
}
